import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, Grid, TextField, FormHelperText } from "@mui/material";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import { useAuth } from "hooks/useAuth";
import { useDOUploadFile } from "hooks";
import FileDropzone from "components/Shared/FileDropzone";
import {
  licenceTypeOptions,
  licenceStatusOptions,
} from "data/static/staticOptions";

const INITIAL_STATE = {
  licenceNumber: "",
  licenceIdd: "",
  issueDate: "",
  expiryDate: "",
  type: "",
  status: "",
  isActive: true,
  attachments: [],
};

const DriverLicenceForm = ({
  isBusy = false,
  driverLicence = null,
  onSubmit = () => {},
  onCancel = () => {},
}) => {
  const { handleUploadFiles } = useDOUploadFile();
  const { viewEntity } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm({
    defaultValues: INITIAL_STATE,
  });

  useEffect(() => {
    if (!driverLicence) return;
    reset({
      ...INITIAL_STATE,
      ...driverLicence,
      issueDate: formatDate(driverLicence.issueDate),
      expiryDate: formatDate(driverLicence.expiryDate),
    });
  }, [driverLicence, reset]);

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const onFormSubmit = async (data) => {
    // Date validation
    if (data.issueDate && data.expiryDate) {
      const issueDate = new Date(data.issueDate);
      const expiryDate = new Date(data.expiryDate);
      if (issueDate > expiryDate) {
        setError("issueDate", {
          type: "manual",
          message: "Issue Date cannot be later than Expiry Date",
        });
        setError("expiryDate", {
          type: "manual",
          message: "Expiry Date cannot be earlier than Issue Date",
        });
        return; // Prevent form submission
      }
    }

    const [existingAttachments, newAttachments] = data.attachments.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    let uploadedAttachments = existingAttachments;

    if (newAttachments.length > 0) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "DRIVER_LICENCE",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });
      uploadedAttachments = [...existingAttachments, ...uploadedFiles];
    }

    onSubmit({ ...data, attachments: uploadedAttachments });
    reset(INITIAL_STATE);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} padding={3}>
      <Grid container spacing={2}>
        {!driverLicence && (
          <Grid item xs={12} md={12}>
            <Controller
              name="licenceNumber"
              control={control}
              rules={{ required: "Please select a Licence Number." }}
              render={({ field }) => (
                <TextField
                  required
                  fullWidth
                  label="Licence Number"
                  type="string"
                  {...field}
                  helperText={errors?.licenceNumber?.message}
                  error={!!errors?.licenceNumber}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} md={12}>
          <Controller
            name="issueDate"
            control={control}
            rules={{ required: "Please select an Issue Date." }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="Issue Date"
                type="date"
                {...field}
                helperText={errors?.issueDate?.message}
                error={!!errors?.issueDate}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="expiryDate"
            control={control}
            rules={{ required: "Please select an Expiry Date." }}
            render={({ field }) => (
              <TextField
                required
                fullWidth
                label="Expiry Date"
                type="date"
                {...field}
                helperText={errors?.expiryDate?.message}
                error={!!errors?.expiryDate}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="type"
            control={control}
            rules={{ required: "Please select a Licence Type." }}
            render={({ field }) => (
              <BaseSelect
                fullWidth
                required
                label="Type"
                type="string"
                options={licenceTypeOptions}
                {...field}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <Controller
            name="status"
            control={control}
            rules={{ required: "Please select a Status." }}
            render={({ field }) => (
              <BaseSelect
                fullWidth
                required
                label="Status"
                type="string"
                options={licenceStatusOptions}
                {...field}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <FormHelperText>Attachments</FormHelperText>
          <Controller
            name="attachments"
            control={control}
            render={({ field }) => (
              <FileDropzone
                multiple
                maxFiles={5}
                files={field.value || []}
                onFileChange={(files) => field.onChange(files)}
              />
            )}
          />
        </Grid>

        {driverLicence && (
          <Grid item xs={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <AppSwitch {...field} label="Is Active?" />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={isBusy}>
              Submit
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DriverLicenceForm;